import { ensureValidLocale } from '@opallabs/lambda'
import { Zones } from './raw_data'
import type { IANATimezoneIdentifier, Options } from './types'

type FormattedZones = Map<IANATimezoneIdentifier, string>

type TimeZoneDataProps = Readonly<
  Options & {
    withRegionName?: boolean
  }
>

/**
 * Convert raw `Zones` data into presentational form. The list formatting is
 * localized using `locale`.
 *
 * If `withRegionName` is `true`, the region name is appended to each example,
 * localized using `locale`.
 *
 * @example
 * timeZoneData().get("Europe/Kyiv")
 * //=> "Athens, Cairo, Kyiv"
 *
 * @example
 * timeZoneData({ locale: "ja", withRegionName: true }).get("Europe/Kyiv")
 * //=> "Athens (ギリシャ) Cairo (エジプト) Kyiv (ウクライナ)"
 */
export function timeZoneData({
  locale = navigator.language,
  withRegionName = false,
}: TimeZoneDataProps = {}): FormattedZones {
  ensureValidLocale(locale)

  const regionName = new Intl.DisplayNames(locale, { type: 'region' })
  const formatted: FormattedZones = new Map()

  for (const [zoneName, examples] of Object.entries(Zones)) {
    const formattedExamples = Object.entries(examples).map(([city, zone]) =>
      withRegionName && zone.region
        ? `${city} (${regionName.of(zone.region)})`
        : city
    )
    const listJoiner = new Intl.ListFormat(locale, {
      style: 'short',
      type: 'unit',
    })
    formatted.set(zoneName, listJoiner.format(formattedExamples))
  }

  return formatted
}
